import React, { useState, useEffect } from 'react';
import "../pages/css/DashBoard.css";
import Header from '../pages/components/Header';
import SideBar from '../pages/components/SideBar';
import { getDatabase, ref, get, update } from "firebase/database";
import { getAuth } from "firebase/auth";
import UserVerification from '../pages/components/UserVerification';
import { useNavigate } from "react-router-dom";
import { Table, Button } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaCheckCircle, FaTimesCircle, FaMoneyBill } from 'react-icons/fa';
import Swal from 'sweetalert2';

function UserPaymentVerification() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [pendingPayments, setPendingPayments] = useState([]);
    const navigate = useNavigate();

    UserVerification();

    const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

    useEffect(() => {
        const db = getDatabase();
        const usersRef = ref(db, "blackstone-users/");

        get(usersRef).then(snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const payments = [];

                Object.keys(data).forEach(uid => {
                    const user = data[uid];
                    const rechargeDetails = user.rechargeDetails || {};

                    Object.keys(rechargeDetails).forEach(key => {
                        const payment = rechargeDetails[key];
                        if (payment.status === "pending") {
                            payments.push({
                                uid,
                                username: user.username || "N/A",
                                email: user.email || "N/A",
                                phone: user.phone || "N/A",
                                ...payment,
                                key, // store generated key for status updates
                            });
                        }
                    });
                });

                setPendingPayments(payments);
            }
        }).catch(error => {
            console.error("Error fetching users:", error);
        });
    }, []);

    // ✅ Change Status Function
    const handleStatusChange = async (uid, key, currentStatus) => {
        const db = getDatabase();
        const paymentRef = ref(db, `blackstone-users/${uid}/rechargeDetails/${key}`);
        const userWalletRef = ref(db, `blackstone-users/${uid}/walletBalance`);
        const newStatus = currentStatus === "pending" ? "verified" : "pending";

        try {
            // Update the recharge payment status
            await update(paymentRef, { status: newStatus });

            // If verified, add amount to wallet balance
            if (newStatus === "verified") {
                const walletSnap = await get(userWalletRef);
                const currentBalance = walletSnap.exists() ? walletSnap.val() : 0;
                const paymentSnap = await get(paymentRef);
                const paymentAmount = paymentSnap.val().amount || 0;

                await update(ref(db, `blackstone-users/${uid}`), {
                    walletBalance: currentBalance + paymentAmount
                });
            }

            // Update frontend state
            setPendingPayments(prev =>
                prev.map(p => p.uid === uid && p.key === key ? { ...p, status: newStatus } : p)
            );

            Swal.fire(
                "✅ Success",
                `Payment status changed to ${newStatus}${newStatus === "verified" ? " and wallet balance updated." : ""}`,
                "success"
            );

        } catch (error) {
            console.error("Error updating status or wallet balance:", error);
            Swal.fire("❌ Error", "Failed to update status or wallet balance", "error");
        }
    };

    // ✅ View Recharge Details
    const handleViewDetails = (payment) => {
        Swal.fire({
            title: "💰 Recharge Details",
            html: `
      <p><strong>Amount:</strong> ₹${payment.amount?.toLocaleString() || "N/A"}</p>
      <p><strong>TDR ID:</strong> ${payment.tdrId || "N/A"}</p>
      <p><strong>Transaction ID:</strong> ${payment.transactionId || "N/A"}</p>
      <p><strong>Timestamp:</strong> ${payment.timestamp || "N/A"}</p>
    `,
            icon: "info",
            confirmButtonText: "Close"
        });
    };

    return (
        <>
            <div className="d-flex layout-container">
                <SideBar isOpen={isSidebarOpen} />

                <div className="flex-grow-1">
                    <Header onMenuClick={toggleSidebar} />
                    <div className="scrollable-section">
                        <div className="p-4 shadow-md bg-white rounded" style={{ maxWidth: '1300px' }}>
                            <h3 className="mb-4 fw-bold">Pending Wallet Recharge Payments</h3>

                            <Table bordered hover size="sm" className="custom-ai-table" striped responsive style={{ fontSize: '0.875rem' }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>UID</th>
                                        <th><FaUser /> Username</th>
                                        <th><FaEnvelope /> Email</th>
                                        <th><FaPhone /> Phone</th>
                                        <th>Transaction ID</th>
                                        <th>TDR ID</th>
                                        <th>Amount (INR)</th>
                                        <th>Status</th>
                                        <th>Change Status</th>
                                        <th>View Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingPayments.length > 0 ? pendingPayments.map(payment => (
                                        <tr key={`${payment.uid}-${payment.key}`}>
                                            <td>{payment.uid}</td>
                                            <td>{payment.username}</td>
                                            <td>{payment.email}</td>
                                            <td>{payment.phone}</td>
                                            <td>{payment.transactionId || "N/A"}</td>
                                            <td>{payment.tdrId || "N/A"}</td>
                                            <td>₹{payment.amount?.toLocaleString() || "N/A"}</td>
                                            <td>
                                                {payment.status === "verified" ? (
                                                    <span className="text-success fw-bold">Verified</span>
                                                ) : (
                                                    <span className="text-warning fw-bold">Pending</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant={payment.status === "verified" ? "warning" : "success"}
                                                    onClick={() => handleStatusChange(payment.uid, payment.key, payment.status)}
                                                >
                                                    {payment.status === "verified" ? "Set Pending" : "Set Verified"}
                                                </Button>
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="info"
                                                    onClick={() => handleViewDetails(payment)}
                                                >
                                                    View Details
                                                </Button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="9" className="text-center">No pending payments found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default UserPaymentVerification;
